import React, { useEffect } from "react";
import {
	Box,
	Container,
	Grid,
	Typography,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Link,
	useMediaQuery,
	Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Icons
import MapIcon from "@mui/icons-material/Map";
import InstagramIcon from "@mui/icons-material/Instagram";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import GavelIcon from "@mui/icons-material/Gavel";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TranslateIcon from "@mui/icons-material/Translate";

// Theme
import { useTheme } from "@mui/material/styles";
import { gradients, colors } from "../Theme/theme";
import { FIRM } from "../constants/firm";
import PrivacyPolicy from "../components/Nav/PrivacyPolicy";

// Styled components
const FooterContainer = styled(Box)(({ theme }) => ({
	position: "relative",
	overflow: "hidden",
	background: gradients.footer.main,
	color: "#ffffff",
	paddingTop: theme.spacing(8),
	paddingBottom: theme.spacing(4),
	borderTop: `16px solid ${colors.accent.main}`,
	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		background: `
      linear-gradient(
        180deg,
        transparent 0%,
        rgba(26, 71, 137, 0.1) 20%,
        rgba(13, 46, 94, 0.3) 60%,
        rgba(10, 36, 72, 0.5) 100%
      )
    `,
		zIndex: 1,
	},
}));

const FooterPatternOverlay = styled(Box)(({ theme }) => ({
	position: "absolute",
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	background: `
		radial-gradient(
			ellipse at bottom center,
			rgba(162, 192, 255, 0.08) 0%,
			rgba(26, 71, 137, 0.05) 30%,
			transparent 60%
		),
		linear-gradient(
			135deg,
			rgba(250, 212, 97, 0.03) 0%,
			transparent 25%,
			transparent 75%,
			rgba(162, 192, 255, 0.03) 100%
		)
	`,
	opacity: 0.8,
	zIndex: 1,
}));

const FooterContent = styled(Container)(({ theme }) => ({
	position: "relative",
	zIndex: 3,
}));

const FooterHeading = styled(Typography)(({ theme }) => ({
	color: "#ffffff",
	fontWeight: 600,
	marginBottom: theme.spacing(3),
	position: "relative",
	paddingBottom: theme.spacing(1),
	"&:after": {
		content: '""',
		position: "absolute",
		bottom: 0,
		left: 0,
		width: "40px",
		height: "3px",
		background: "#FAD461",
		borderRadius: "2px",
	},
}));

const FooterItem = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "flex-start",
	marginBottom: theme.spacing(2),
}));

const FooterIcon = styled(Box)(({ theme }) => ({
	marginRight: theme.spacing(2),
	color: "#FAD461",
	marginTop: "2px",
}));

const SocialButtonContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	marginTop: theme.spacing(2),
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
	backgroundColor: "rgba(255, 255, 255, 0.1)",
	color: "#ffffff",
	marginRight: theme.spacing(1),
	padding: theme.spacing(1),
	transition: "all 0.3s ease",
	"&:hover": {
		backgroundColor: "#FAD461",
		color: "#1a4789",
		transform: "translateY(-3px)",
	},
}));

const FooterLink = styled(Link)(({ theme }) => ({
	color: "#ffffff",
	textDecoration: "none",
	position: "relative",
	transition: "all 0.3s ease",
	"&:hover": {
		color: "#FAD461",
		textDecoration: "none",
	},
}));

const PracticeItem = styled(ListItem)(({ theme }) => ({
	padding: theme.spacing(0.5, 0),
	transition: "all 0.3s ease",
	"&:hover": {
		transform: "translateX(5px)",
	},
}));

const PracticeLink = styled(Link)(({ theme }) => ({
	color: "#ffffff",
	textDecoration: "none",
	display: "flex",
	alignItems: "center",
	"&:hover": {
		color: "#FAD461",
		"& .MuiSvgIcon-root": {
			opacity: 1,
			transform: "translateX(2px)",
		},
	},
}));

const PracticeLinkIcon = styled(ArrowForwardIcon)(({ theme }) => ({
	fontSize: "0.8rem",
	marginLeft: theme.spacing(1),
	opacity: 0,
	transition: "all 0.3s ease",
}));

const ZiaSymbol = styled(Box)(({ theme }) => ({
	position: "absolute",
	right: "5%",
	bottom: "10%",
	opacity: 0.15,
	zIndex: 2,
	[theme.breakpoints.down("md")]: {
		display: "none",
	},
}));

const BottomBar = styled(Box)(({ theme }) => ({
	borderTop: "1px solid rgba(255, 255, 255, 0.1)",
	paddingTop: theme.spacing(2),
	marginTop: theme.spacing(4),
}));

const Footer = () => {
	const theme = useTheme();
	const [open, setOpen] = React.useState(false);
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	// Animation controls
	const controls = useAnimation();
	const [ref, inView] = useInView({
		triggerOnce: true,
		threshold: 0.1,
	});

	useEffect(() => {
		if (inView) {
			controls.start("visible");
		}
	}, [controls, inView]);

	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.8,
				ease: "easeOut",
			},
		},
	};

	// Practice areas data
	const practiceAreas = [
		{ title: "Auto Accidents", href: "#Practice" },
		{ title: "Premises Liability", href: "#Practice" },
		{ title: "Medical Malpractice", href: "#Practice" },
		{ title: "DUI/DWI Accidents", href: "#Practice" },
		{ title: "Dog Bites", href: "#Practice" },
		{ title: "Gun Violence", href: "#Practice" },
	];

	// Quick links data
	const quickLinks = [
		{ title: "Home", href: "#" },
		{ title: "The Team", href: "#About" },
		{ title: "Our Practice", href: "#Practice" },
		{ title: "Contact Us", href: "#Contact" },
	];

	const currentYear = new Date().getFullYear();

	return (
		<FooterContainer ref={ref}>
			<FooterPatternOverlay />
			{/* Zia Symbol Background Element */}
			<ZiaSymbol>
				<motion.img
					src="/ziaSymbol.avif"
					alt=""
					aria-hidden="true"
					loading="lazy"
					width={250}
					height={250}
					animate={{
						rotate: [0, 360],
					}}
					transition={{
						duration: 120,
						ease: "linear",
						repeat: Infinity,
					}}
				/>
			</ZiaSymbol>
			<FooterContent maxWidth="xl">
				<motion.div variants={containerVariants} initial="hidden" animate={controls}>
					<Grid container spacing={isMobile ? 4 : 6}>
						{/* Contact Information */}
						<Grid item xs={12} sm={6} md={4}>
							<motion.div variants={itemVariants}>
								<FooterHeading variant="h6">Contact Us</FooterHeading>

								<FooterItem>
									<FooterIcon>
										<MapIcon fontSize="small" />
									</FooterIcon>
									<Box>
										<Typography variant="body2" sx={{ lineHeight: 1.6 }}>
											{FIRM.address.street}
											<br />
											{FIRM.address.city}, {FIRM.address.state} {FIRM.address.zip}
										</Typography>
									</Box>
								</FooterItem>

								<FooterItem>
									<FooterIcon>
										<PhoneIcon fontSize="small" />
									</FooterIcon>
									<Box>
										<FooterLink href={FIRM.phone.href} variant="body2">
											{FIRM.phone.display}
										</FooterLink>
										<Typography
											variant="caption"
											sx={{ display: "block", color: "rgba(255,255,255,0.7)" }}
										>
											Phone
										</Typography>
									</Box>
								</FooterItem>

								<FooterItem>
									<FooterIcon>
										<EmailIcon fontSize="small" />
									</FooterIcon>
									<Box>
										<Typography variant="body2">{FIRM.fax.display}</Typography>
										<Typography
											variant="caption"
											sx={{ display: "block", color: "rgba(255,255,255,0.7)" }}
										>
											Fax
										</Typography>
									</Box>
								</FooterItem>

								<SocialButtonContainer>
									<SocialButton
										href={FIRM.social.instagram}
										target="_blank"
										aria-label="Instagram"
									>
										<InstagramIcon fontSize="small" />
									</SocialButton>
									<SocialButton
										href={FIRM.social.googleMaps}
										target="_blank"
										aria-label="Google Maps"
									>
										<MapIcon fontSize="small" />
									</SocialButton>
								</SocialButtonContainer>
							</motion.div>
						</Grid>

						{/* Practice Areas */}
						<Grid item xs={12} sm={6} md={3}>
							<motion.div variants={itemVariants}>
								<FooterHeading variant="h6">Practice Areas</FooterHeading>
								<List disablePadding>
									{practiceAreas.map((area) => (
										<PracticeItem key={area.title} disableGutters>
											<PracticeLink href={area.href}>
												<GavelIcon sx={{ mr: 1, fontSize: "0.8rem" }} />
												<ListItemText
													primary={area.title}
													primaryTypographyProps={{ variant: "body2" }}
												/>
												<PracticeLinkIcon />
											</PracticeLink>
										</PracticeItem>
									))}
								</List>
							</motion.div>
						</Grid>

						{/* Quick Links */}
						<Grid item xs={12} sm={6} md={2}>
							<motion.div variants={itemVariants}>
								<FooterHeading variant="h6">Quick Links</FooterHeading>
								<List disablePadding>
									{quickLinks.map((link) => (
										<ListItem key={link.title} disableGutters sx={{ pb: 0.5 }}>
											<FooterLink href={link.href}>
												<Typography variant="body2">{link.title}</Typography>
											</FooterLink>
										</ListItem>
									))}
									<ListItem key={"privacy-policy"} disableGutters sx={{ pb: 0.5 }}>
										<FooterLink sx={{ cursor: "pointer" }} onClick={handleClickOpen}>
											<Typography variant="body2">Privacy Policy</Typography>
										</FooterLink>
									</ListItem>
								</List>
							</motion.div>
						</Grid>

						{/* About & Logo */}
						<Grid item xs={12} sm={6} md={3}>
							<motion.div variants={itemVariants}>
								<FooterHeading variant="h6">About David Rhee Injury Law</FooterHeading>
								<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
									We provide high-quality legal services to the people of New Mexico. Our firm is
									dedicated to serving clients who have suffered injury due to the negligence or
									wrongful acts of others.
								</Typography>
								<Box
									sx={{
										display: "inline-flex",
										alignItems: "center",
										gap: 1.25,
										px: 1.5,
										py: 1,
										backgroundColor: "rgba(250, 212, 97, 0.12)",
										border: "1px solid rgba(250, 212, 97, 0.3)",
										borderLeft: "3px solid #FAD461",
									}}
								>
									<TranslateIcon sx={{ fontSize: "1rem", color: "#FAD461", flexShrink: 0 }} />
									<Box>
										<Typography
											sx={{
												fontSize: "0.82rem",
												fontWeight: 700,
												color: "#FAD461",
												lineHeight: 1.2,
												fontFamily: "'Heebo', sans-serif",
												letterSpacing: "0.03em",
											}}
										>
											Hablamos Español
										</Typography>
										<Typography
											sx={{
												fontSize: "0.63rem",
												color: "rgba(255,255,255,0.5)",
												letterSpacing: "0.07em",
												textTransform: "uppercase",
												fontFamily: "'Heebo', sans-serif",
											}}
										>
											Bilingual service available
										</Typography>
									</Box>
								</Box>

								<Box sx={{ mt: 3 }}>
									<motion.img
										src="/DRLawLogo2025.png"
										alt="David Rhee Law Logo"
										style={{
											maxWidth: "160px",
											height: "auto",
											filter: "brightness(1.2) drop-shadow(0px 5px 10px rgba(0,0,0,0.2))",
										}}
										whileHover={{ scale: 1.05 }}
										transition={{ duration: 0.3 }}
									/>
								</Box>
							</motion.div>
						</Grid>
					</Grid>
				</motion.div>

				{/* Bottom Bar with Copyright */}
				<BottomBar>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} md={6}>
							<Typography variant="body2" color="rgba(255,255,255,0.7)">
								© {currentYear} Law Office of David Rhee. All Rights Reserved.
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography
								variant="caption"
								color="rgba(255,255,255,0.7)"
								sx={{ textAlign: { xs: "left", md: "right" }, display: "block" }}
							>
								This website is attorney advertising and is not meant as legal advice. Prior results do
								not guarantee similar outcomes.
							</Typography>
						</Grid>
					</Grid>
				</BottomBar>
			</FooterContent>
			<PrivacyPolicy handleClose={handleClose} open={open} />
		</FooterContainer>
	);
};

export default Footer;
